/**
 * Från adress (webb) eller skärm (fältapp) till den vy som mäts.
 *
 * Modulnyckeln är behörighetsnyckeln, så att användningsgraden kan räknas
 * mot vem som har behörighet. Delar två menyposter samma nyckel (HMS och
 * Skyddsronder på ia_system, Tidsredovisning och Utlägg på time_reporting)
 * får den andra posten ett `vy`-namn så att de kan visas var för sig.
 *
 * Modullistan (data/modules.js) är sanningskällan, så en ny modul mäts
 * automatiskt. v1 hade en handskriven lista som saknade 20 av 65 moduler.
 */
import { MODULES } from '../data/modules';

// Äldre vägar som inte finns som egna poster i modullistan.
const AVLEDDA_PREFIX = [
  ['/home/overview', 'dashboard'],
  ['/dashboard', 'dashboard'],
  ['/inbox', 'inbox'],
  ['/training', 'onboarding'],
  ['/qa', 'qa_controls'],
  ['/bim', 'bim_viewer'],
  ['/management-system', 'management_system'],
  ['/ice', 'management_system'],
  ['/knowledge', 'kanai'],
  ['/ia', 'ia_system'],
  ['/tenders', 'tenders'],
  ['/issues', 'issues'],
];

const PROJEKT_UNDERSIDOR = {
  tidplan: 'tidplan',
  dashboard: 'dashboard',
  inskrivning: 'inskrivning',
  economy: 'ekonomi',
  'buyer-view': 'bestallarvy',
};

function slug(to) {
  return String(to || '').replace(/^\/+/, '').replace(/\//g, '-');
}

let kandidater = null;
function modulKandidater() {
  if (kandidater) return kandidater;
  const forsta = new Map();
  MODULES.forEach((m) => {
    if (m.moduleKey && !forsta.has(m.moduleKey)) forsta.set(m.moduleKey, m.to);
  });
  kandidater = MODULES
    .filter((m) => m.moduleKey && !m.moduleKey.startsWith('p.') && m.to !== '/projects' && !m.adminOnly && !m.tenantAdminOnly)
    .map((m) => ({
      to: String(m.to || ''),
      moduleKey: m.moduleKey,
      vy: forsta.get(m.moduleKey) === m.to ? null : slug(m.to),
    }))
    .filter((k) => k.to.length > 1)
    // Längst sökväg vinner, annars skuggar /kanprofil /kanprofil/laganalys.
    .sort((a, b) => b.to.length - a.to.length);
  return kandidater;
}

function traffar(pathname, to) {
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * @param {string} pathname
 * @param {string} hash
 * @returns {{moduleKey: string, vy?: string|null, flik?: string|null, projectId?: number|null}|null}
 */
export function webbVy(pathname, hash) {
  const p = pathname || '/';
  if (p === '/' ) return { moduleKey: 'dashboard' };

  if (p === '/projects') return { moduleKey: 'projects', flik: 'lista' };
  const projekt = p.match(/^\/projects\/(\d+)(?:\/([^/]+))?/);
  if (projekt) {
    const projectId = Number(projekt[1]);
    const under = projekt[2];
    const flikHash = String(hash || '').replace(/^#/, '');
    const flik = under ? (PROJEKT_UNDERSIDOR[under] || under) : (flikHash || 'dashboard');
    return { moduleKey: 'projects', flik: flik.slice(0, 60), projectId };
  }

  const traff = modulKandidater().find((k) => traffar(p, k.to));
  if (traff) return { moduleKey: traff.moduleKey, vy: traff.vy };

  const avledd = AVLEDDA_PREFIX.find(([to]) => traffar(p, to));
  if (avledd) return { moduleKey: avledd[1] };
  return null;
}

// Fältappens skärmar. Samma vy-namn som webben så att webb och fält slås
// ihop per modul och bara skiljs åt av källan.
const FALT = {
  home: { moduleKey: 'dashboard' },
  time: { moduleKey: 'time_reporting' },
  utlagg: { moduleKey: 'time_reporting', vy: 'utlagg' },
  'utlagg-attest': { moduleKey: 'time_reporting', vy: 'utlagg' },
  projekt: { moduleKey: 'projects', flik: 'dashboard' },
  'egenkontroll-list': { moduleKey: 'projects', flik: 'egenkontroll' },
  'egenkontroll-check': { moduleKey: 'projects', flik: 'egenkontroll' },
  arbetsberedning: { moduleKey: 'qa_controls', vy: 'arbetsberedningar' },
  dagbok: { moduleKey: 'projects', flik: 'dagbok' },
  'dagbok-ritning': { moduleKey: 'projects', flik: 'dagbok' },
  dokument: { moduleKey: 'projects', flik: 'dokument' },
  ritningsvisare: { moduleKey: 'projects', flik: 'ritningar' },
  rapport: { moduleKey: 'projects', flik: 'rapport' },
  skyddsrond: { moduleKey: 'ia_system', vy: 'skyddsronder' },
  schaktrond: { moduleKey: 'projects', flik: 'schaktrond' },
  'ue-lista': { moduleKey: 'ue_lista' },
  tidplan: { moduleKey: 'projects', flik: 'tidplan' },
  spatial: { moduleKey: 'spatial' },
  'vra-list': { moduleKey: 'projects', flik: 'vra' },
  'vra-record': { moduleKey: 'projects', flik: 'vra' },
  'chatt-list': { moduleKey: 'projects', flik: 'kommunikation' },
  'chatt-thread': { moduleKey: 'projects', flik: 'kommunikation' },
  kanai: { moduleKey: 'kanai' },
  projektkarta: { moduleKey: 'project_map' },
  regler: { moduleKey: 'insight' },
  utbildning: { moduleKey: 'onboarding' },
  bil: { moduleKey: 'fleet' },
  kontakter: { moduleKey: 'contacts' },
  resurser: { moduleKey: 'resource_planning' },
};

const FALT_MED_PROJEKT = new Set(['projects']);

/**
 * @param {string} skarm  FieldApp:s aktuella skärm-id
 * @param {number|string|null} projectId  valt projekt i fältappen
 */
export function faltVy(skarm, projectId) {
  const vy = FALT[skarm];
  if (!vy) return null;
  const pid = Number(projectId);
  return FALT_MED_PROJEKT.has(vy.moduleKey) && Number.isFinite(pid) && pid > 0 ? { ...vy, projectId: pid } : vy;
}
